import { Observable, Subscription } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'
import { delay, find, repeatWhen } from 'rxjs/operators'
import { LanguageErrors } from '../../../../i18n'
import { INVOKE_ID_HEADER } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../../NonReact/utils/URLBuilder'
import { AsyncRESTOptions } from '../Types/Types'
import { RESTAPIError, StatusCodes } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { TimingOptions } from '../../../NonReact/common/TimingOptions'
import { handleError } from '../../../NonReact/common/API/REST/RESTErrorHandler'
import { redirectIfSessionExpired } from '../../../NonReact/utils/SessionUtils'

const ALLOWED_NO_CONTENT_STATUS_CODES: number[] = [StatusCodes.CONTINUE, StatusCodes.OK_NO_CONTENT]

const prepareFetchOptions = ({ headers, body, method }: AsyncRESTOptions): RequestInit => ({
    method,
    headers: {
        'Content-Type': 'application/json',
        ...headers
    },
    body: JSON.stringify(body),
    credentials: 'include'
})

const language: CLanguageHandler = CLanguageHandler.getInstance()

/**
 * Make Async REST API calls and return the response or throw a REST API error.
 *
 * @param url - Initial URL to call in order to start the async process.
 * @param statusURL - URL to call to check the status of the async process.
 * @param options - Options to control the async process.
 *
 * @returns <T> - The response from the async process.
 */
export const requestJSONAsync = <T extends unknown>(
    url: URLConstructor,
    statusURL: URLConstructor,
    options: AsyncRESTOptions
): Promise<Nullable<T>> => {
    language.loadBundle(LanguageErrors.NAME)

    const { maxChecks, frequency, errorMessage }: TimingOptions = options?.timingOptions ?? new TimingOptions()

    return new Promise<Nullable<T>>((resolve, reject): void => {
        fetch(url.constructURL(), prepareFetchOptions(options)).then(
            async (response: Response): Promise<void | Subscription> => {
                if (response.status === StatusCodes.OK) {
                    try {
                        return resolve(response.json() as T)
                    } catch (e) {
                        return reject(
                            new RESTAPIError(
                                StatusCodes.OK,
                                null,
                                language.get(LanguageErrors.RestJsonProcessing),
                                null
                            )
                        )
                    }
                }

                if (
                    ![
                        ...ALLOWED_NO_CONTENT_STATUS_CODES,
                        ...(options?.additionalAllowableNoContentCodes ?? [])
                    ].includes(response.status)
                ) {
                    const error: RESTAPIError = await handleError(response)
                    if (redirectIfSessionExpired(error)) return undefined
                    // Else: page is unloading — don't reject the promise.
                    return reject(error)
                }

                const invokeID: Nullable<string> = response.headers.get(INVOKE_ID_HEADER)

                if (IsStringEmpty(invokeID)) {
                    const error: RESTAPIError = await handleError(response)
                    if (redirectIfSessionExpired(error)) return undefined
                    // Else: page is unloading — don't reject the promise.
                    return reject(error)
                }

                return fromFetch(
                    statusURL.appendPathParams(invokeID ?? '').constructURL(),
                    prepareFetchOptions({ ...options, method: RESTMethods.GET, body: undefined } as AsyncRESTOptions)
                )
                    .pipe(
                        repeatWhen((obs: Observable<Response>): Observable<Response> => obs.pipe(delay(frequency))),
                        find(
                            (statusResponse: Response, index: number): boolean =>
                                statusResponse.status !== StatusCodes.CONTINUE || index + 1 >= maxChecks
                        )
                    )
                    .subscribe((statusResponse: Nullable<Response>): void => {
                        const contentLength: Nullable<string> = statusResponse?.headers.get('Content-Length')

                        if (
                            [...(options?.additionalAllowableNoContentCodes ?? []), StatusCodes.OK_NO_CONTENT].includes(
                                statusResponse?.status ?? StatusCodes.INTERNAL_SERVER_ERROR
                            ) &&
                            (IsStringEmpty(contentLength) || contentLength === '0') &&
                            !options.errorOnNoContent
                        ) {
                            return resolve(null)
                        }

                        if (statusResponse?.status === StatusCodes.OK) {
                            return resolve(statusResponse.json() as T)
                        }

                        if (statusResponse?.status === StatusCodes.CONTINUE) {
                            return reject(new RESTAPIError(StatusCodes.CONTINUE, null, errorMessage, null))
                        }

                        handleError(statusResponse).then((error: RESTAPIError): void => {
                            if (!redirectIfSessionExpired(error)) reject(error)
                            // Else: page is unloading — don't reject the promise.
                        })

                        return undefined
                    })
            }
        )
    })
}
